import { useCallback, useEffect, useRef, useState } from "react";
import data from "../consts/data.json";
import toastr from "toastr";
import KeyOption from "../interfaces/KeyOption.interface";
import {
  sortArrayByKeyOrderOrRandom,
  validateSelection,
} from "../utils/app.util";

export type UseQuestionManagerReturnType = {
  question: KeyOption;
  options: KeyOption[];
  answeredQuestions: string[];
  handleSelect: (option: string) => void;
  count: number;
  loading: boolean;
};

const toastrOptions = {
  error: {
    timeOut: 1000,
  },
  success: {},
};

const useQuestionManager = (): UseQuestionManagerReturnType => {
  const pentagramsRef = useRef(
    data.keys.filter((e) => e.category === "pentagram")
  );
  const namesRef = useRef(data.keys.filter((e) => e.category === "name"));

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<string[]>([]);
  const [question, setQuestion] = useState<KeyOption>({
    id: "",
    image: "",
    value: "",
    category: "",
  });
  const [options, setOptions] = useState<KeyOption[]>([]);

  const loadImages = useCallback(async () => {
    const imageUrls: string[] = [];

    for (const key of data.keys) {
      const image = new Image();
      image.src = key.image;

      await new Promise<void>((resolve) => {
        image.onload = () => {
          imageUrls.push(key.image);
          resolve();
        };
      });
    }

    setLoading(false);
  }, []);

  const selectRandomQuestion = useCallback(() => {
    const availableQuestions = data.keys.filter(
      (question) => !answeredQuestions.includes(question.value)
    );

    if (availableQuestions.length === 0) {
      setAnsweredQuestions(() => []);
      return;
    }

    const randomIndex = Math.floor(Math.random() * availableQuestions.length);
    const randomElement = availableQuestions[randomIndex];

    setQuestion(randomElement);

    let optionsArray: KeyOption[] = [];

    switch (randomElement.category) {
      case "pentagram":
        const majorMinorImagesPentagram = ["Maj", "min"];
        const randomImageTypePentagram =
          majorMinorImagesPentagram[
            Math.floor(Math.random() * majorMinorImagesPentagram.length)
          ];

        optionsArray = namesRef.current.filter((option) =>
          option.image.includes(randomImageTypePentagram)
        );
        break;
      case "name":
        optionsArray = sortArrayByKeyOrderOrRandom(pentagramsRef.current);
        break;
      case "sharps":
        optionsArray = sortArrayByKeyOrderOrRandom(data.sharps, true);
        break;
      case "flats":
        optionsArray = sortArrayByKeyOrderOrRandom(data.flats, true);
        break;
      default:
        optionsArray = [];
    }

    setOptions(optionsArray);
  }, [answeredQuestions]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  useEffect(selectRandomQuestion, [answeredQuestions, selectRandomQuestion]);

  const handleSelect = (option: string): void => {
    if (!validateSelection(option, question.value)) {
      toastr.error("Wrong", "", toastrOptions.error);
      setCount(0);
      return;
    }

    setAnsweredQuestions((prevAnsweredQuestions) => [
      ...prevAnsweredQuestions,
      option,
    ]);

    setCount(count + 1);
    toastr.clear();
    toastr.success("Correct");
  };

  return {
    question,
    options,
    answeredQuestions,
    handleSelect,
    count,
    loading,
  };
};

export default useQuestionManager;
